'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PortalLink, useUserResourceSource, BlockIcon } from '@akademi/site-builder-runtime';
import { Button, Card, CardContent, CardHeader, CardTitle, SelectField } from '@akademi/ui';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { mpSurface, isCartTotalZero, isCartEmpty } from './_shared';
import { tx } from '../_shared/coerce';
import { useMarketplaceCart } from './_useMarketplaceCart';

type Localized = string | Record<string, string>;

const CONTINUE_WITHOUT_ADDRESS_VALUE = '__continue_without_address__';

export interface MarketplaceAddressSelectProps {
  title?: Localized;
  icon?: string;
  myAddressHref?: string;
  allowContinueWithout?: boolean;
  /** Show this block even when the live cart's total is already zero — a
   *  free/fully-discounted checkout has nothing to ship or invoice.
   *  Ignored with no marketplace source mounted. */
  showWhenFree?: boolean;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_ADDRESSES = [
  { id: 'home', label: 'Home — 123 Example St, Istanbul' },
  { id: 'work', label: 'Work — 45 Business Ave, Ankara' },
];

/** Address-selection card — akademi-lms-v2's cart checkout address step
 *  (an async-search select there). With {@link useUserResourceSource}'s
 *  `addresses` namespace mounted, lists the signed-in user's real saved
 *  addresses (the same `/v1/geo/address/search` `/profile-settings`'s
 *  address tab uses — not marketplace-scoped, so this reads off
 *  `UserResourceSource` rather than `MarketplaceSource`); with no source
 *  it falls back to the authored `addresses` list. */
export function MarketplaceAddressSelect({
  title,
  icon = 'map',
  myAddressHref = '/profile-settings?step=my-address',
  allowContinueWithout = true,
  showWhenFree = false,
  paddingOuter,
  paddingInner,
}: MarketplaceAddressSelectProps) {
  const { t } = useTranslation('site-builder-blocks');
  const userResource = useUserResourceSource();
  const addressSource = userResource?.addresses;
  const cart = useMarketplaceCart();

  const [fetched, setFetched] = useState<{ id: string; label: string }[] | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  useEffect(() => {
    if (!addressSource) return;
    let cancelled = false;
    setStatus('loading');
    addressSource
      .list()
      .then((rows) => {
        if (cancelled) return;
        setFetched(
          rows.map((a) => ({
            id: String(a.id),
            label:
              [a.name, [a.addressLine1, a.district, a.city].filter(Boolean).join(', ')]
                .filter(Boolean)
                .join(' — ') || String(a.id),
          })),
        );
        setStatus('idle');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [addressSource]);

  const list = addressSource ? (fetched ?? []) : DEFAULT_ADDRESSES;
  const [value, setValue] = useState<string | undefined>(undefined);

  if (cart && isCartEmpty(cart.cart)) return null;
  if (!showWhenFree && cart && isCartTotalZero(cart.cart)) return null;

  const options = [
    ...list.map((a) => ({ value: a.id, label: a.label })),
    ...(allowContinueWithout
      ? [{ value: CONTINUE_WITHOUT_ADDRESS_VALUE, label: t('marketplace.address.continueWithout') }]
      : []),
  ];

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
        <CardHeader className="p-0">
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}>
            <BlockIcon value={icon} size={18} />
            {tx(title) || t('marketplace.address.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-3 flex flex-col gap-3">
          <SelectField
            value={value}
            onChange={setValue}
            options={options}
            disabled={addressSource ? status === 'loading' : false}
            placeholder={
              addressSource && status === 'loading'
                ? t('marketplace.address.loading', { defaultValue: 'Loading…' })
                : t('marketplace.address.placeholder')
            }
            ariaLabel={t('marketplace.address.title')}
            fullWidth
          />

          <div className="flex justify-end gap-2">
            <Button asChild variant="brand-outlined" size="xs" className="w-fit">
              <PortalLink href={myAddressHref}>{t('marketplace.address.myAddress')}</PortalLink>
            </Button>
          </div>
        </CardContent>
      </Card>
    </SectionShell>
  );
}
